/**
 * @fileoverview Idempotency key registry for Sales CRM commands.
 * @description Where the Idempotency-Keys of Sales commands are kept: one registry per QueryClient,
 * which outlives any dialog, so a key issued by a dialog that has since closed (or unmounted
 * mid-request) is still there when it is reopened and the command retried. A key belongs to the
 * command the person meant (its action and exact input), so a retry with the same values replays
 * it while a changed command takes a new key. A new QueryClient (a new test, a new session) starts
 * with no keys.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type { QueryClient } from '@tanstack/react-query'

const registries = new WeakMap<QueryClient, Map<string, string>>()

/**
 * @description The scope a command's key is kept under: the action and its exact input.
 * @param {string} action - The command, e.g. `edit-prospect`.
 * @param {unknown} input - The command's variables (ids, ETag and values).
 * @returns {string} The scope: one per action and input.
 */
export function idempotencyScope(action: string, input: unknown): string {
  return `${action}:${JSON.stringify(input)}`
}

/**
 * @description Returns the key registry of a query client, making it on first use.
 * @param {QueryClient} queryClient - The app's (or the test's) query client.
 * @returns {Map<string, string>} The keys in use, by scope.
 */
export function idempotencyKeysOf(queryClient: QueryClient): Map<string, string> {
  let registry = registries.get(queryClient)
  if (!registry) {
    registry = new Map()
    registries.set(queryClient, registry)
  }
  return registry
}
